import * as React from 'react'
import { FC, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'

import { AgreementActionsButton } from '@user/components/Agreements/common/AgreementActionsButton'
import {
  currencyString,
  ENTITY_TYPES,
  splitCamelCase,
} from '@sales-center/common'
import { DashboardAgreement } from '@user/store/dashboard/widgets/manageAgreements/typings'
import {
  selectManageAgreements,
  setPage,
} from '@user/store/dashboard/widgets/manageAgreements/manageAgreements'
import { useAppDispatch } from '@user/store/hooks/useAppDispatch'
import { useCheckPermissions } from '@user/store/authorization/permissions'

import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'
import Pagination from '@mui/material/Pagination'
import { useLocalizationDateFormat } from '@user/features/Localization/useLocalizationDateFormat'
import { styles } from '../../ClosedWonQueueSubmissions/ContentFront/SoldServiceListCard/styling'
import Button from '@mui/material/Button'
import { useData } from '@user/components/Dashboard/widgets/ManageAgreements/AgreementList/hooks/useData'
import { AgreementFormPacketDialog } from '@user/components/Agreements/AgreementFormPacketDialog/AgreementFormPacketDialog'
import { ServiceDialog } from '@user/components/Services'

interface Props {
  agreements: DashboardAgreement[]
}

export const AgreementList: FC<Props> = (props) => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { agreements } = props
  const { count, take, page } = useSelector(selectManageAgreements)
  const { formatDate } = useLocalizationDateFormat()
  const {
    createDrawerOpen,
    formPacketEnabled,
    openAgreementCreateDrawer,
    agreement,
    closeCreateDrawer,
    cancelPopupOpen,
    onClose,
    svcOpen,
    onCloseSvc,
    onOpenSvc,
    hideCancelDialog,
    refreshAgreementList,
    statusValue,
  } = useData()

  const [canUpdateLeads, canUpdateOpps] = useCheckPermissions(
    'leads_update',
    'opportunities_update',
  )
  const canUpdateEntity = useCallback(
    (entityType: string) =>
      entityType == 'Lead' ? canUpdateLeads : canUpdateOpps,
    [canUpdateLeads, canUpdateOpps],
  )

  const goToEntityDetail = useCallback(
    (entityId: number, entityType: string) => {
      const entityPrefix = entityType == 'Lead' ? 'leads' : 'opportunities'
      navigate(`/${entityPrefix}/${entityId}`)
    },
    [navigate],
  )

  return (
    <>
      <Grid container direction="column">
        {agreements.map((agreement) => (
          <Box sx={{ ...styles.tarjeta, pr: '40px' }} key={agreement.formId}>
            <Box
              display="flex"
              flexDirection="column"
              style={{ width: '100%' }}
            >
              <Box
                display="flex"
                flexDirection="row"
                justifyContent="space-between"
              >
                <Box sx={styles.name}>
                  <Button
                    sx={styles.textButton}
                    variant="text"
                    data-test-id={`manage-agreements-entity-button-${agreement.entityId}`}
                    onClick={() =>
                      goToEntityDetail(agreement.entityId, agreement.entityType)
                    }
                  >
                    {agreement.contact}
                  </Button>
                </Box>
                <Box
                  display="flex"
                  flexDirection="row"
                  justifyContent="space-between"
                  alignItems="center"
                  mr="-40px"
                  gap={0.5}
                >
                  <Box sx={styles.status}>
                    {splitCamelCase(agreement.status)}
                  </Box>
                  <AgreementActionsButton
                    canUpdate={canUpdateEntity(agreement.entityType)}
                    entityId={agreement.entityId}
                    entityType={
                      agreement.entityType == 'Lead'
                        ? ENTITY_TYPES.Lead
                        : ENTITY_TYPES.Opportunity
                    }
                    id={agreement.formId}
                    onUpdateStatus={() => refreshAgreementList(statusValue)}
                    status={agreement.status}
                    openAgreementCreateDrawer={() =>
                      openAgreementCreateDrawer(agreement)
                    }
                  />
                </Box>
              </Box>

              <Box
                display="flex"
                flexDirection="row"
                justifyContent="space-between"
              >
                <Box sx={styles.total}>
                  ${currencyString(agreement.totalValue)}
                </Box>
                <Box sx={styles.date}>
                  Last modified on: {formatDate(agreement.modifiedDate, 'date')}
                </Box>
              </Box>
            </Box>
          </Box>
        ))}
      </Grid>
      <Box display="flex" justifyContent="flex-end" mt={1}>
        <Pagination
          count={Math.ceil(count / take)}
          color="primary"
          page={page}
          onChange={(_e, page) => {
            dispatch(setPage(page))
          }}
        />
      </Box>

      {formPacketEnabled && createDrawerOpen && agreement && (
        <AgreementFormPacketDialog
          entityId={agreement.entityId}
          entityType={
            agreement.entityType === 'Lead'
              ? ENTITY_TYPES.Lead
              : ENTITY_TYPES.Opportunity
          }
          onClose={closeCreateDrawer}
          onCloseFormManager={onClose}
          open={createDrawerOpen}
          openServicesDrawer={onOpenSvc}
          cancelPopupOpen={cancelPopupOpen}
          handleCancel={onClose}
          cancelPopupClose={hideCancelDialog}
        />
      )}

      {svcOpen && agreement && (
        <ServiceDialog
          address={''}
          branchId={''}
          entityId={agreement.entityId}
          entityType={
            agreement.entityType === 'Lead'
              ? ENTITY_TYPES.Lead
              : ENTITY_TYPES.Opportunity
          }
          minimumServicesCount={agreement.entityType === 'Lead' ? 0 : 1}
          deleteServiceWarningMessage={
            agreement.entityType === 'Lead'
              ? ''
              : 'Opportunities require at least one product or service'
          }
          open={svcOpen}
          onClose={onCloseSvc}
          openAddServiceDrawerOnMount={false}
          locationSource="existing"
        />
      )}
    </>
  )
}

AgreementList.displayName = 'AgreementList'
